import React from "react";

export interface TimelineMeta {
  total?: number | null;
  network?: number | null;
  server?: number | null;
  request?: number | null;
  ttft?: number | null;
}

interface RequestTimelineProps {
  meta: TimelineMeta;
  timestamps?: {
    client_start_time?: number;
    server_start_time?: number;
    request_start_time?: number;
    first_token_time?: number;
    request_end_time?: number;
    server_end_time?: number;
    client_end_time?: number;
  };
}

interface TimeSegment {
  id: string;
  label: string;
  startTime: number;
  endTime: number;
  duration: number;
  color: string;
}

interface SummarySegment {
  id: string;
  label: string;
  startOffset: number;
  duration: number;
  color: string;
}

const RequestTimeline: React.FC<RequestTimelineProps> = ({
  meta,
  timestamps,
}) => {
  const t = timestamps;
  if (!t || !t.client_start_time || !t.client_end_time) return null;

  const roundTime = (time?: number) => (time ? Math.round(time) : 0);
  const baseTime = t.client_start_time;
  const normalizeTime = (time?: number) => roundTime(time) - roundTime(baseTime);

  const totalDuration = normalizeTime(t.client_end_time);

  // Calculate minimum width percentage
  const getMinWidthPercentage = (duration: number) => {
    const calculatedWidth = (duration / totalDuration) * 100;
    return duration > 0 ? Math.max(calculatedWidth, 2) : 0;
  };

  // Calculate all segment durations
  const clientToServerDuration = normalizeTime(t.server_start_time) - normalizeTime(t.client_start_time);
  const serverToRequestDuration = normalizeTime(t.request_start_time) - normalizeTime(t.server_start_time);
  const requestDuration = normalizeTime(t.request_end_time) - normalizeTime(t.request_start_time);
  const requestToFirstTokenDuration = t.first_token_time ? normalizeTime(t.first_token_time) - normalizeTime(t.request_start_time) : 0;
  const requestToServerEndDuration = normalizeTime(t.server_end_time) - normalizeTime(t.request_end_time);
  const serverToClientDuration = normalizeTime(t.client_end_time) - normalizeTime(t.server_end_time);
  const serverTotalDuration = normalizeTime(t.server_end_time) - normalizeTime(t.server_start_time);
  const networkTotalDuration = clientToServerDuration + serverToClientDuration;

  // Create horizontally stacked segments
  const stackedSegments: TimeSegment[] = [
    {
      id: "client_to_server",
      label: "Client → Server",
      startTime: 0,
      endTime: clientToServerDuration,
      duration: clientToServerDuration,
      color: "#4682B4", // SteelBlue
    },
    {
      id: "server_to_request",
      label: "Server → Request",
      startTime: clientToServerDuration,
      endTime: clientToServerDuration + serverToRequestDuration,
      duration: serverToRequestDuration,
      color: "#66CDAA", // MediumAquamarine
    },
    {
      id: "request_duration",
      label: "Request",
      startTime: clientToServerDuration + serverToRequestDuration,
      endTime: clientToServerDuration + serverToRequestDuration + requestDuration,
      duration: requestDuration,
      color: "#FF7F50", // Coral
    },
    {
      id: "request_to_server_end",
      label: "Request → Server End",
      startTime: clientToServerDuration + serverToRequestDuration + requestDuration,
      endTime: clientToServerDuration + serverToRequestDuration + requestDuration + requestToServerEndDuration,
      duration: requestToServerEndDuration,
      color: "#9370DB", // MediumPurple
    },
    {
      id: "server_to_client",
      label: "Server → Client",
      startTime: clientToServerDuration + serverToRequestDuration + requestDuration + requestToServerEndDuration,
      endTime: totalDuration,
      duration: serverToClientDuration,
      color: "#4682B4", // SteelBlue
    },
  ].filter((segment) => segment.duration > 0);

  // Create summary segments (positioned bars)
  const summarySegments: SummarySegment[] = [
    {
      id: "total",
      label: "Total",
      startOffset: 0,
      duration: totalDuration,
      color: "#7B68EE", // SlateBlue
    },
    {
      id: "network",
      label: "Network",
      startOffset: 0,
      duration: networkTotalDuration,
      color: "#4682B4", // SteelBlue
    },
    {
      id: "server",
      label: "Server",
      startOffset: clientToServerDuration,
      duration: serverTotalDuration,
      color: "#20B2AA", // LightSeaGreen
    },
    {
      id: "request",
      label: "Request",
      startOffset: clientToServerDuration + serverToRequestDuration,
      duration: requestDuration,
      color: "#FF7F50", // Coral
    },
    ...(t.first_token_time
      ? [
          {
            id: "ttft",
            label: "TTFT",
            startOffset: clientToServerDuration + serverToRequestDuration,
            duration: requestToFirstTokenDuration,
            color: "#FFD700", // Gold
          },
        ]
      : []),
  ];

  return (
    <div className="w-full p-4 box-border overflow-hidden">
      <h3 className="text-lg font-semibold mb-6">Request Timeline</h3>

      {/* Stacked Bar */}
      <div className="mb-8">
        <div className="flex h-6 w-full rounded overflow-hidden shadow-sm">
          {stackedSegments.map((segment) => (
            <div
              key={segment.id}
              title={`${segment.label}: ${segment.duration}ms`}
              style={{
                width: `${getMinWidthPercentage(segment.duration)}%`,
                backgroundColor: segment.color,
                opacity: 0.9,
                cursor: "pointer",
                transition: "opacity 0.15s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.9")}
            />
          ))}
        </div>
      </div>

      {/* Summary Bars */}
      <div>
        {summarySegments.map((segment) => (
          <div key={segment.id} className="mb-4 flex items-center h-6">
            <div className="w-20 mr-3 text-base text-[#666]">{segment.label}</div>

            <div className="flex-1 flex items-center min-w-0">
              <div className="relative h-4 w-full bg-[#f5f5f5] rounded-lg overflow-hidden">
                <div
                  title={`${segment.duration}ms (${((segment.duration / totalDuration) * 100).toFixed(1)}% of total)`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: `${(segment.startOffset / totalDuration) * 100}%`,
                    height: "100%",
                    width: `${getMinWidthPercentage(segment.duration)}%`,
                    backgroundColor: segment.color,
                    cursor: "pointer",
                    borderRadius: "8px",
                    transition: "opacity 0.15s ease",
                    opacity: 0.9,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.9")}
                />
              </div>
            </div>

            <div className="ml-3 w-14 text-sm text-right text-[#666] whitespace-nowrap">
              {segment.duration}ms
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RequestTimeline;


